import React, { useEffect, useMemo, useState } from 'react'

import { GUESTGET, httpOk, resolveUploadUrl } from 'helpers/api'

function WebsitePreview({ domain }) {
	const [config, setConfig] = useState(null)
	const [menu, setMenu] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const normalizedDomain = normalizeDomain(domain)

	async function load() {
		if (!normalizedDomain) {
			setConfig(null)
			setMenu([])
			setError('Укажите домен и сохраните сайт, чтобы открыть preview.')
			return
		}

		setLoading(true)
		setError('')

		const settingsResponse = await GUESTGET(`/website-settings/domain/${encodeURIComponent(normalizedDomain)}`)
		if (!httpOk(settingsResponse)) {
			setLoading(false)
			setConfig(null)
			setMenu([])
			setError('Не удалось загрузить настройки сайта.')
			return
		}

		const settings = settingsResponse.data?.data ?? settingsResponse.data
		setConfig(settings)

		const defaultPos = settings?.poses?.find(pos => pos.is_default) ?? settings?.poses?.[0]
		const posId = defaultPos?.id
		if (!posId) {
			setLoading(false)
			setMenu([])
			setError('Для preview не найдена торговая точка.')
			return
		}

		const menuResponse = await GUESTGET(`/website-menu/domain/${encodeURIComponent(normalizedDomain)}?pos_id=${posId}`)
		if (httpOk(menuResponse)) {
			setMenu(extractMenu(menuResponse.data))
		}

		setLoading(false)
	}

	useEffect(() => {
		load()
	}, [normalizedDomain]) // eslint-disable-line react-hooks/exhaustive-deps

	const colors = config?.colors ?? {}
	const primaryColor = colors.primary?.default ?? '#e31e24'
	const primaryTextColor = colors.primary?.text ?? '#ffffff'
	const backgroundColor = colors.background?.default ?? '#f7f4ef'
	const mutedBackgroundColor = colors.background?.muted ?? '#f1f5f9'
	const cardColor = colors.background?.card ?? '#ffffff'
	const textColor = colors.text?.default ?? '#1f2933'
	const mutedColor = colors.text?.muted ?? '#6b7280'
	const categories = useMemo(() => Array.isArray(menu) ? menu : [], [menu])
	const products = useMemo(() => flattenProducts(categories).slice(0, 12), [categories])
	const brandName = config?.brand_name || config?.name || normalizedDomain || 'Mison'

	if (loading) {
		return <div className="card"><div className="card-body text-center py-5">Загрузка preview...</div></div>
	}

	if (error) {
		return (
			<div className="card">
				<div className="card-body text-center py-5">
					<h5 className="mb-2">Preview недоступен</h5>
					<p className="text-muted mb-0">{error}</p>
				</div>
			</div>
		)
	}

	if (!config) {
		return null
	}

	return (
		<div className="card">
			<div className="card-body p-0">
				<div style={{ background: backgroundColor, color: textColor, borderRadius: 12, overflow: 'hidden' }}>
					<div className="px-4 px-lg-5 py-3 d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ background: cardColor }}>
						<div className="d-flex align-items-center gap-3">
							<div className="d-flex align-items-center justify-content-center" style={{ width: 58, height: 58, borderRadius: 18, background: mutedBackgroundColor, boxShadow: '0 10px 25px rgba(15, 23, 42, .08)' }}>
								{config.logo
									? <img src={resolveUploadUrl(config.logo, 'websites')} alt={brandName} style={{ maxWidth: 42, maxHeight: 42, objectFit: 'contain' }} />
									: <b style={{ color: primaryColor }}>{brandName.slice(0, 1)}</b>
								}
							</div>
							<div>
								<div className="fw-bold" style={{ fontSize: 22 }}>{brandName}</div>
								<div style={{ color: mutedColor }}>{config.description || 'Доставка любимых блюд'}</div>
							</div>
						</div>
						<div className="d-flex align-items-center gap-2 flex-wrap">
							<button type="button" className="btn rounded-pill px-3" style={{ background: mutedBackgroundColor, color: textColor }}>Доставка</button>
							<button type="button" className="btn rounded-pill px-3" style={{ background: mutedBackgroundColor, color: textColor }}>Самовывоз</button>
							<button type="button" className="btn rounded-pill px-4" style={{ background: primaryColor, color: primaryTextColor }}>Корзина</button>
						</div>
					</div>

					<div className="px-4 px-lg-5 py-4">
						{!!categories.length &&
							<div className="d-flex gap-2 overflow-auto pb-2 mb-4">
								{categories.map((category, index) => (
									<button
										type="button"
										className="btn rounded-pill px-4"
										key={category.id ?? category.name ?? index}
										style={{
											background: index === 0 ? primaryColor : cardColor,
											color: index === 0 ? primaryTextColor : textColor,
											boxShadow: '0 10px 25px rgba(15, 23, 42, .08)',
											whiteSpace: 'nowrap',
										}}>
										{category.name}
									</button>
								))}
							</div>
						}

						<section className="mb-4">
							<div className="d-flex justify-content-between align-items-end mb-3">
								<div>
									<h2 className="mb-1">Хит продаж</h2>
									<div style={{ color: mutedColor }}>Популярные позиции из меню сайта</div>
								</div>
								<button type="button" className="btn btn-link text-decoration-none" style={{ color: primaryColor }}>Все меню</button>
							</div>

							{products.length
								? <div className="row g-3">
									{products.map(product => (
										<div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={`${product.product_id ?? product.id}-${product.name}`}>
											<div className="h-100 p-3 d-flex flex-column" style={{ background: cardColor, borderRadius: 28, boxShadow: '0 14px 35px rgba(15, 23, 42, .08)' }}>
												<div className="mb-3" style={{ height: 190, borderRadius: 22, background: mutedBackgroundColor, overflow: 'hidden' }}>
													{product.image &&
														<img src={resolveUploadUrl(product.image, 'products')} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
													}
												</div>
												<h5 className="mb-2">{product.name}</h5>
												<p className="mb-3 flex-grow-1" style={{ color: mutedColor, minHeight: 44 }}>
													{product.description || 'Вкусная позиция из вашего меню'}
												</p>
												<div className="d-flex justify-content-between align-items-center gap-3">
													<div className="fw-bold" style={{ color: primaryColor, fontSize: 18 }}>
														{formatPrice(product.sale_price ?? product.price)}
													</div>
													<button type="button" className="btn rounded-circle" style={{ background: primaryColor, color: primaryTextColor, width: 42, height: 42 }}>+</button>
												</div>
											</div>
										</div>
									))}
								</div>
								: <div className="p-4 text-center" style={{ background: cardColor, borderRadius: 24, color: mutedColor }}>Меню пока пустое.</div>
							}
						</section>
					</div>
				</div>
			</div>
		</div>
	)
}

function normalizeDomain(domain) {
	if (!domain) return ''
	return String(domain)
		.trim()
		.toLowerCase()
		.replace(/^https?:\/\//, '')
		.replace(/\/.*$/, '')
}

function extractMenu(payload) {
	const data = payload?.data ?? payload
	if (Array.isArray(data)) return data
	if (Array.isArray(data?.menu)) return data.menu
	if (Array.isArray(data?.categories)) return data.categories
	return []
}

function flattenProducts(categories = []) {
	return categories.flatMap(category => [
		...(category.products ?? []),
		...flattenProducts(category.children ?? []),
	])
}

function formatPrice(value) {
	return `${Number(value ?? 0).toLocaleString('ru-RU')} сум`
}

export default WebsitePreview
